/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { WritableAtom, atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { addOrUpdateEntityItem, removeEntityId } from 'jotai/entity';
import { activeWorkspaceAtom } from 'jotai/workspace';
import { VisualizationSettingsObject } from './VisualizationSettingsObject';
import { upgrade as upgradeVisualizationSettings } from 'jotai/visualizationSettings';
import { workspaceService } from 'services/workspace/WorkspaceService';

export const visualizationSettingIdsAtom = atom<string[], [string[]], void>(
  (get) => {
    const currentWorkspace = get(activeWorkspaceAtom);
    if (Object.keys(currentWorkspace).length > 0) {
      return [...currentWorkspace.workspace.visualizationSettings.allIds];
    } else {
      return [];
    }
  },
  (get, set, newValue: string[]) => {
    const previousWorkspace = get(activeWorkspaceAtom);
    previousWorkspace.workspace.visualizationSettings.allIds = newValue;
    set(activeWorkspaceAtom, { ...previousWorkspace });
  }
);

export const visualizationSettingsAtomFamily = atomFamily<
  string,
  WritableAtom<VisualizationSettingsObject, [VisualizationSettingsObject], void>
>((id: string) =>
  atom<VisualizationSettingsObject, [VisualizationSettingsObject], void>(
    (get) => {
      const currentWorkspace = get(activeWorkspaceAtom);
      if (Object.keys(currentWorkspace).length !== 0) {
        const settings = currentWorkspace.workspace.visualizationSettings.byId[id];
        // This addresses a defect where visualization settings in preformatted workspaces were not provided their ids.
        if (settings && !Object.hasOwn(settings, 'id')) {
          settings.id = id;
        }
        const visualization = Object.values(currentWorkspace.workspace.visualizations.byId).find(
          (visualization) => visualization.settings === id
        );
        return visualization ? upgradeVisualizationSettings(settings, visualization.type) : settings;
      }
    },
    (get, set, visualizationSettings: VisualizationSettingsObject) => {
      const currentWorkspace = get(activeWorkspaceAtom);
      if (Object.keys(visualizationSettings).length === 0) {
        removeEntityId(currentWorkspace.workspace.visualizationSettings, id);
        set(activeWorkspaceAtom, { ...currentWorkspace });
      } else {
        addOrUpdateEntityItem(
          currentWorkspace.workspace.visualizationSettings,
          visualizationSettings,
          visualizationSettings.id
        );
        set(activeWorkspaceAtom, { ...currentWorkspace });
        workspaceService.updateWorkspace(currentWorkspace.workspace);
      }
    }
  )
);

export const defaultRaceOrderTableSettingsAtom = atom<boolean>(true);
export const isCurrentNumberOfCheckedInvalidAtom = atom<boolean>(false);
